import { Device } from '@twilio/voice-sdk';
import VoiceAPI from 'dashboard/api/voice.js';

export const CALL_LOCAL_STATUS = {
  started: 'started',
  in_process: 'in_process',
  ended: 'ended',
  error: 'error',
};

export const TWILIO_EVENTS = {
  INCOMING_CALL: 'twilio-incoming',
  CALL_ACCEPTED: 'twilio-call-accepted',
  CALL_REJECTED: 'twilio-call-rejected',
  CALL_CANCELLED: 'twilio-call-cancelled',
  CALL_DISCONNECTED: 'twilio-call-disconnected',
  CALL_ERROR: 'twilio-call-error',
  CALL_MUTED: 'twilio-call-muted',
  CALL_UNMUTED: 'twilio-call-unmuted',
  CALL_VOLUME: 'twilio-call-volume',
  CALL_RINGING: 'twilio-call-ringing',
  CALL_RECONNECTING: 'twilio-call-reconnecting',
  CALL_RECONNECTED: 'twilio-call-reconnected',
  DEVICE_READY: 'twilio-device-ready',
  DEVICE_ERROR: 'twilio-device-error',
  DEVICE_REGISTERED: 'twilio-device-registered',
  DEVICE_UNREGISTERED: 'twilio-device-unregistered',
  DEVICE_OFFLINE: 'twilio-device-offline',
  TOKEN_WILL_EXPIRE: 'twilio-token-will-expire',
  TOKEN_EXPIRED: 'twilio-token-expired',

  OUTGOING_CALL_ACCEPTED: 'twilio-outgoing-call-accepted',
  OUTGOING_CALL_ERROR: 'twilio-outgoing-call-error',
  OUTGOING_CALL_DISCONNECTED: 'twilio-outgoing-call-disconnected',
};

class TwilioService {
  constructor() {
    this.device = null;
  }

  async init(token) {
    if (!token) {
      throw new Error('Twilio access token is required');
    }

    if (this.device) {
      return this.device; // already initialized
    }

    this.device = new Device(token, {
      logLevel: 'error',
      codecPreferences: ['opus', 'pcmu'],
    });

    this._registerEvents();

    // Important: register device to receive incoming calls
    this.device.register();

    return this.device;
  }

  _registerEvents() {
    // Clear old listeners to avoid duplicates
    this.device.removeAllListeners();

    // ---- Device events ----
    this.device.addListener('ready', () => {
      console.log('Twilio Device is ready for calls');
      window.bus.$emit(TWILIO_EVENTS.DEVICE_READY);
    });

    this.device.addListener('error', error => {
      console.error('Twilio Device error:', error);
      window.bus.$emit(TWILIO_EVENTS.DEVICE_ERROR, error);
    });

    this.device.addListener('registered', () => {
      console.log('Device registered with Twilio');
      window.bus.$emit(TWILIO_EVENTS.DEVICE_REGISTERED);
    });

    this.device.addListener('unregistered', () => {
      console.log('Device unregistered');
      window.bus.$emit(TWILIO_EVENTS.DEVICE_UNREGISTERED);
    });

    this.device.addListener('offline', () => {
      console.log('Device went offline');
      window.bus.$emit(TWILIO_EVENTS.DEVICE_OFFLINE);
    });

    this.device.addListener('tokenWillExpire', async () => {
      console.log('Token will expire soon, refreshing...');
      window.bus.$emit(TWILIO_EVENTS.TOKEN_WILL_EXPIRE);

      try {
        const { data } = await VoiceAPI.generate_tokens();
        await this.device.updateToken(data.token);
        console.log('Token refreshed successfully');
      } catch (err) {
        console.error('Failed to refresh token:', err);
      }
    });

    this.device.addListener('tokenExpired', async () => {
      console.log('Token has expired.');
      window.bus.$emit(TWILIO_EVENTS.TOKEN_EXPIRED);
      try {
        const { data } = await VoiceAPI.generate_tokens();
        await this.device.updateToken(data.token);
        console.log('Token refreshed successfully');
      } catch (err) {
        console.error('Failed to refresh token:', err);
      }
    });

    // ---- Incoming Call ----
    this.device.addListener('incoming', call => {
      console.log('Incoming call:', call);
      console.log(call.parameters);
      window.bus.$emit(TWILIO_EVENTS.INCOMING_CALL, call);

      // One-time events
      call.once('accept', () => {
        console.log('Call accepted');
        window.bus.$emit(TWILIO_EVENTS.CALL_ACCEPTED, call);
      });

      call.once('reject', () => {
        console.log('Call rejected');
        window.bus.$emit(TWILIO_EVENTS.CALL_REJECTED, call);
      });

      call.once('cancel', () => {
        console.log('Caller hung up before answer || Missed call');
        window.bus.$emit(TWILIO_EVENTS.CALL_CANCELLED, call);
      });

      call.once('disconnect', () => {
        console.log('Call disconnected');
        window.bus.$emit(TWILIO_EVENTS.CALL_DISCONNECTED, call);
      });

      call.once('error', err => {
        console.error('Call error:', err);
        window.bus.$emit(TWILIO_EVENTS.CALL_ERROR, err);
      });

      // Continuous events
      call.on('mute', () => {
        console.log('Muted');
        window.bus.$emit(TWILIO_EVENTS.CALL_MUTED, call);
      });

      call.on('unmute', () => {
        console.log('Unmuted');
        window.bus.$emit(TWILIO_EVENTS.CALL_UNMUTED, call);
      });

      // call.on("volume", (inputVolume, outputVolume) => {
      //   console.log("Volume levels", { inputVolume, outputVolume });
      //   window.bus.$emit(TWILIO_EVENTS.CALL_VOLUME, { inputVolume, outputVolume });
      // });

      call.on('ringing', () => {
        console.log('The remote party is ringing');
        window.bus.$emit(TWILIO_EVENTS.CALL_RINGING, call);
      });

      call.on('reconnecting', () => {
        console.log('Call is reconnecting...');
        window.bus.$emit(TWILIO_EVENTS.CALL_RECONNECTING, call);
      });

      call.on('reconnected', () => {
        console.log('Call reconnected');
        window.bus.$emit(TWILIO_EVENTS.CALL_RECONNECTED, call);
      });
    });

    // end events
  }

  getDevice() {
    return this.device;
  }

  async connect(params = {}) {
    if (!this.device) {
      throw new Error('Twilio Device is not initialized');
    }
    console.log('calling to ', params);
    const call = await this.device.connect({ params });
    console.log(call, 'Call instance connect');

    // Attach outgoing call events
    call.on('ringing', () => {
      console.log('📞 Outgoing call is ringing...');
      // window.bus.$emit(TWILIO_EVENTS.CALL_RINGING, call);
    });

    call.on('accept', () => {
      console.log('✅ Outgoing call accepted!');
      window.bus.$emit(TWILIO_EVENTS.OUTGOING_CALL_ACCEPTED, call);
    });

    call.on('disconnect', () => {
      console.log('❌ Outgoing call disconnected');
      window.bus.$emit(TWILIO_EVENTS.OUTGOING_CALL_DISCONNECTED, call);
    });

    call.on('error', err => {
      console.error('⚠️ Outgoing call error:', err);
      window.bus.$emit(TWILIO_EVENTS.OUTGOING_CALL_ERROR, err);
    });

    return call;
  }

  disconnectAll() {
    if (this.device) {
      this.device.disconnectAll();
    }
  }

  destroy() {
    if (this.device) {
      this.device.removeAllListeners();
      this.device.destroy();
      this.device = null;
    }
  }
}

// Singleton instance
const twilioService = new TwilioService();
export default twilioService;
